import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import {
	Alert,
	Box,
	Button,
	Chip,
	CircularProgress,
	Container,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControl,
	FormControlLabel,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Radio,
	RadioGroup,
	Select,
	Table,
	TableBody,
	TableCell,
	TableHead,
	TableRow,
	TextField,
	Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import UploadFileIcon from "@mui/icons-material/UploadFile";

const API = process.env.REACT_APP_LAMBDA_ARTEH_API_DOMAIN;

/**
 * Scope 3 activity entries (backlog S-02/S-03/S-05) with CSV import (S-04).
 * Three methods in descending data quality: supplier-specific factor,
 * average-data factor, spend x economy-wide intensity. The method is stored
 * on each record and disclosed per category in the AASB S2 report.
 */
export const GHG_CATEGORIES: { id: number; name: string }[] = [
	{ id: 1, name: "Purchased goods & services" },
	{ id: 2, name: "Capital goods" },
	{ id: 3, name: "Fuel- and energy-related activities" },
	{ id: 4, name: "Upstream transportation & distribution" },
	{ id: 5, name: "Waste generated in operations" },
	{ id: 6, name: "Business travel" },
	{ id: 7, name: "Employee commuting" },
	{ id: 8, name: "Upstream leased assets" },
	{ id: 9, name: "Downstream transportation & distribution" },
	{ id: 10, name: "Processing of sold products" },
	{ id: 11, name: "Use of sold products" },
	{ id: 12, name: "End-of-life treatment of sold products" },
	{ id: 13, name: "Downstream leased assets" },
	{ id: 14, name: "Franchises" },
	{ id: 15, name: "Investments" },
];

interface Entry {
	entryID: number;
	year: number;
	ghgCategoryID: number;
	method: "supplier" | "average" | "spend";
	description: string;
	supplierName: string | null;
	quantity: number | null;
	unit: string | null;
	factorValue: number | null;
	factorSource: string | null;
	spendAUD: number | null;
	tCO2e: number;
}

interface CustomFactor {
	factorID: number;
	name: string;
	unit: string | null;
	factorValue: number;
	isActive?: number;
}

const methodLabel = (m: Entry["method"]) =>
	m === "supplier"
		? "Supplier-specific"
		: m === "average"
			? "Average-data"
			: "Spend-based";

const emptyForm = () => ({
	ghgCategoryID: 1,
	method: "average" as Entry["method"],
	description: "",
	supplierName: "",
	quantity: "",
	unit: "",
	factorValue: "",
	factorSource: "",
	spendAUD: "",
	customFactorID: "",
});

/** Minimal CSV parse: quoted fields, commas, CRLF. Good enough for AP exports. */
export function parseCsv(text: string): string[][] {
	const rows: string[][] = [];
	let row: string[] = [];
	let cell = "";
	let inQuotes = false;
	for (let i = 0; i < text.length; i++) {
		const ch = text[i];
		if (inQuotes) {
			if (ch === '"' && text[i + 1] === '"') {
				cell += '"';
				i++;
			} else if (ch === '"') inQuotes = false;
			else cell += ch;
		} else if (ch === '"') inQuotes = true;
		else if (ch === ",") {
			row.push(cell);
			cell = "";
		} else if (ch === "\n" || ch === "\r") {
			if (ch === "\r" && text[i + 1] === "\n") i++;
			row.push(cell);
			cell = "";
			if (row.some((c) => c.trim() !== "")) rows.push(row);
			row = [];
		} else cell += ch;
	}
	row.push(cell);
	if (row.some((c) => c.trim() !== "")) rows.push(row);
	return rows;
}

const MAPPING_FIELDS = [
	{ key: "description", label: "Description", required: true },
	{ key: "spendAUD", label: "Spend (AUD)", required: true },
	{ key: "ghgCategoryID", label: "GHG category (1-15)", required: false },
	{ key: "supplierName", label: "Supplier name", required: false },
	{ key: "factorValue", label: "Factor (kg CO2e/AUD)", required: false },
] as const;

const Scope3Entries: React.FC = () => {
	const { getAccessTokenSilently } = useAuth0();
	const thisYear = new Date().getFullYear();
	const [year, setYear] = useState(thisYear - 1);
	const [entries, setEntries] = useState<Entry[]>([]);
	const [factors, setFactors] = useState<CustomFactor[]>([]);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [notice, setNotice] = useState<string | null>(null);
	const [dialogOpen, setDialogOpen] = useState(false);
	const [form, setForm] = useState(emptyForm());
	const [saving, setSaving] = useState(false);
	const [csvOpen, setCsvOpen] = useState(false);
	const [csvRows, setCsvRows] = useState<string[][]>([]);
	const [csvMapping, setCsvMapping] = useState<Record<string, number | "">>({});
	const [csvCategory, setCsvCategory] = useState(1);
	const [csvFactor, setCsvFactor] = useState("0.25");
	const [importing, setImporting] = useState(false);

	const authFetch = useCallback(
		async (path: string, init?: RequestInit) => {
			const token = await getAccessTokenSilently();
			return fetch(`${API}${path}`, {
				...init,
				headers: {
					Authorization: `Bearer ${token}`,
					"Content-Type": "application/json",
					...(init?.headers || {}),
				},
			});
		},
		[getAccessTokenSilently],
	);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const [eRes, fRes] = await Promise.all([
				authFetch(`/api/scope3-entries?year=${year}`),
				authFetch("/api/custom-factors"),
			]);
			const eJson = await eRes.json().catch(() => ({}));
			if (!eRes.ok || eJson.Ok === false)
				throw new Error(eJson.Message || "Could not load entries");
			setEntries((eJson.Data || eJson).entries || []);
			if (fRes.ok) {
				const fJson = await fRes.json().catch(() => ({}));
				setFactors(
					((fJson.Data || fJson).factors || []).filter(
						(f: CustomFactor) => f.isActive !== 0,
					),
				);
			}
		} catch (e: any) {
			setError(e.message || "Could not load Scope 3 entries");
		} finally {
			setLoading(false);
		}
	}, [authFetch, year]);

	useEffect(() => {
		load();
	}, [load]);

	const save = async () => {
		setSaving(true);
		setError(null);
		try {
			const res = await authFetch("/api/scope3-entries", {
				method: "POST",
				body: JSON.stringify({
					year,
					ghgCategoryID: form.ghgCategoryID,
					method: form.method,
					description: form.description,
					supplierName: form.supplierName || undefined,
					quantity: form.quantity || undefined,
					unit: form.unit || undefined,
					factorValue: form.factorValue || undefined,
					factorSource: form.factorSource || undefined,
					spendAUD: form.spendAUD || undefined,
					customFactorID: form.customFactorID || undefined,
				}),
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false)
				throw new Error(json.Message || "Could not save the entry");
			setDialogOpen(false);
			setForm(emptyForm());
			setNotice(`Saved — ${json.Data.tCO2e} t CO₂e recorded.`);
			await load();
		} catch (e: any) {
			setError(e.message || "Could not save the entry");
		} finally {
			setSaving(false);
		}
	};

	const remove = async (id: number) => {
		try {
			await authFetch(`/api/scope3-entries/${id}`, { method: "DELETE" });
			await load();
		} catch {
			setError("Could not delete the entry");
		}
	};

	const onCsvFile = async (file: File) => {
		const text = await file.text();
		const rows = parseCsv(text);
		if (rows.length < 2) {
			setError("The CSV needs a header row and at least one data row");
			return;
		}
		setCsvRows(rows);
		try {
			const saved = JSON.parse(
				localStorage.getItem("arteh.s3csvMapping") || "null",
			);
			if (
				saved &&
				JSON.stringify(saved.headers) === JSON.stringify(rows[0])
			) {
				setCsvMapping(saved.mapping);
				setCsvCategory(saved.category ?? 1);
				setCsvFactor(saved.factor ?? "0.25");
			} else {
				const guess: Record<string, number | ""> = {};
				rows[0].forEach((h, i) => {
					const n = h.toLowerCase();
					if (/desc|item|detail|narrative/.test(n)) guess.description = i;
					if (/amount|spend|aud|total|value|\$/.test(n) && guess.spendAUD === undefined) guess.spendAUD = i;
					if (/categ/.test(n)) guess.ghgCategoryID = i;
					if (/supplier|vendor|payee/.test(n)) guess.supplierName = i;
					if (/factor|intensity/.test(n)) guess.factorValue = i;
				});
				setCsvMapping(guess);
			}
		} catch {
			/* fresh mapping */
		}
		setCsvOpen(true);
	};

	const runImport = async () => {
		setImporting(true);
		setError(null);
		try {
			const header = csvRows[0];
			const body = csvRows.slice(1).map((row) => {
				const cell = (key: string) => {
					const idx = csvMapping[key];
					return idx === "" || idx === undefined
						? ""
						: (row[idx as number] || "").trim();
				};
				const cat = parseInt(cell("ghgCategoryID"), 10);
				const factor = parseFloat(cell("factorValue"));
				return {
					year,
					ghgCategoryID: cat >= 1 && cat <= 15 ? cat : csvCategory,
					method: "spend" as const,
					description: cell("description"),
					supplierName: cell("supplierName") || undefined,
					spendAUD: cell("spendAUD").replace(/[^0-9.-]/g, ""),
					factorValue:
						isFinite(factor) && factor > 0
							? factor
							: parseFloat(csvFactor),
					factorSource: "AP spend CSV import",
				};
			});
			const res = await authFetch("/api/scope3-entries/bulk", {
				method: "POST",
				body: JSON.stringify({ entries: body }),
			});
			const json = await res.json().catch(() => ({}));
			if (!res.ok || json.Ok === false) {
				let detail = json.Message || "Import failed";
				try {
					const parsed = JSON.parse(json.Message);
					detail =
						`${parsed.message}: ` +
						parsed.errors
							.slice(0, 5)
							.map(
								(e: { index: number; message: string }) =>
									`row ${e.index + 2} — ${e.message}`,
							)
							.join("; ");
				} catch {
					/* plain message */
				}
				throw new Error(detail);
			}
			localStorage.setItem(
				"arteh.s3csvMapping",
				JSON.stringify({
					headers: header,
					mapping: csvMapping,
					category: csvCategory,
					factor: csvFactor,
				}),
			);
			setCsvOpen(false);
			setCsvRows([]);
			setNotice(
				`Imported ${json.Data.imported} entries — ${json.Data.totalTonnes} t CO₂e.`,
			);
			await load();
		} catch (e: any) {
			setError(e.message || "Import failed");
		} finally {
			setImporting(false);
		}
	};

	const years = Array.from({ length: 6 }, (_, i) => thisYear - i);
	const totalTonnes = useMemo(
		() =>
			Math.round(
				entries.reduce((s, e) => s + Number(e.tCO2e || 0), 0) * 1000,
			) / 1000,
		[entries],
	);
	const needsQuantity = form.method !== "spend";

	return (
		<Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
			<Paper elevation={2} sx={{ p: 4 }}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap", mb: 1 }}>
					<Box sx={{ flex: 1, minWidth: 260 }}>
						<Typography variant="h5">Scope 3 entries</Typography>
						<Typography variant="body2" color="text.secondary">
							Record value-chain emissions in any GHG Protocol
							category — supplier-specific factors first, average
							data second, spend estimates last. The method is
							disclosed per category in the AASB S2 report.
						</Typography>
					</Box>
					<FormControl size="small" sx={{ minWidth: 130 }}>
						<InputLabel id="s3e-year">Year</InputLabel>
						<Select labelId="s3e-year" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))}>
							{years.map((y) => (
								<MenuItem key={y} value={y}>{y}</MenuItem>
							))}
						</Select>
					</FormControl>
					<Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
						Import spend CSV
						<input
							hidden
							type="file"
							accept=".csv,text/csv"
							onChange={(e) => {
								const f = e.target.files?.[0];
								if (f) onCsvFile(f);
								e.target.value = "";
							}}
						/>
					</Button>
					<Button variant="contained" startIcon={<AddIcon />} onClick={() => setDialogOpen(true)}>
						Add entry
					</Button>
				</Box>

				{error && (
					<Alert severity="error" sx={{ my: 2 }} onClose={() => setError(null)}>{error}</Alert>
				)}
				{notice && (
					<Alert severity="success" sx={{ my: 2 }} onClose={() => setNotice(null)}>{notice}</Alert>
				)}

				{loading ? (
					<Box sx={{ textAlign: "center", py: 6 }}><CircularProgress /></Box>
				) : entries.length === 0 ? (
					<Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>
						No Scope 3 entries for {year} yet. Add one, or import an AP spend CSV.
					</Typography>
				) : (
					<>
						<Typography variant="subtitle2" sx={{ mb: 1 }}>
							{entries.length} entries · {totalTonnes} t CO₂e
						</Typography>
						<Table size="small">
							<TableHead>
								<TableRow>
									<TableCell>Category</TableCell>
									<TableCell>Method</TableCell>
									<TableCell>Description</TableCell>
									<TableCell>Supplier</TableCell>
									<TableCell align="right">Basis</TableCell>
									<TableCell align="right">t CO₂e</TableCell>
									<TableCell />
								</TableRow>
							</TableHead>
							<TableBody>
								{entries.map((e) => (
									<TableRow key={e.entryID}>
										<TableCell>
											{e.ghgCategoryID}.{" "}
											{GHG_CATEGORIES.find((c) => c.id === e.ghgCategoryID)?.name}
										</TableCell>
										<TableCell>
											<Chip
												size="small"
												label={methodLabel(e.method)}
												color={e.method === "supplier" ? "success" : e.method === "average" ? "info" : "default"}
											/>
										</TableCell>
										<TableCell>{e.description}</TableCell>
										<TableCell>{e.supplierName || "—"}</TableCell>
										<TableCell align="right">
											{e.method === "spend"
												? `$${Number(e.spendAUD).toLocaleString()} × ${e.factorValue}`
												: `${Number(e.quantity).toLocaleString()} ${e.unit} × ${e.factorValue}`}
										</TableCell>
										<TableCell align="right">{Number(e.tCO2e).toLocaleString()}</TableCell>
										<TableCell align="right">
											<IconButton size="small" onClick={() => remove(e.entryID)} aria-label="delete">
												<DeleteOutlineIcon fontSize="small" />
											</IconButton>
										</TableCell>
									</TableRow>
								))}
							</TableBody>
						</Table>
					</>
				)}
			</Paper>

			<Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} fullWidth maxWidth="sm">
				<DialogTitle>Add Scope 3 entry ({year})</DialogTitle>
				<DialogContent dividers sx={{ display: "grid", gap: 2, pt: 2 }}>
					<FormControl size="small" fullWidth sx={{ mt: 1 }}>
						<InputLabel id="s3e-cat">GHG category</InputLabel>
						<Select
							labelId="s3e-cat"
							label="GHG category"
							value={form.ghgCategoryID}
							onChange={(e) => setForm({ ...form, ghgCategoryID: Number(e.target.value) })}
						>
							{GHG_CATEGORIES.map((c) => (
								<MenuItem key={c.id} value={c.id}>{c.id}. {c.name}</MenuItem>
							))}
						</Select>
					</FormControl>
					<RadioGroup
						row
						value={form.method}
						onChange={(e) => setForm({ ...form, method: e.target.value as Entry["method"] })}
					>
						<FormControlLabel value="supplier" control={<Radio size="small" />} label="Supplier-specific" />
						<FormControlLabel value="average" control={<Radio size="small" />} label="Average-data" />
						<FormControlLabel value="spend" control={<Radio size="small" />} label="Spend-based" />
					</RadioGroup>
					<TextField
						size="small"
						label="Description"
						value={form.description}
						onChange={(e) => setForm({ ...form, description: e.target.value })}
						required
					/>
					<TextField
						size="small"
						label="Supplier (optional)"
						value={form.supplierName}
						onChange={(e) => setForm({ ...form, supplierName: e.target.value })}
					/>
					{form.method === "supplier" && factors.length > 0 && (
						<FormControl size="small" fullWidth>
							<InputLabel id="s3e-cf">Supplier factor (from Custom factors)</InputLabel>
							<Select
								labelId="s3e-cf"
								label="Supplier factor (from Custom factors)"
								value={form.customFactorID}
								onChange={(e) => setForm({ ...form, customFactorID: String(e.target.value) })}
							>
								<MenuItem value="">— enter the factor manually —</MenuItem>
								{factors.map((f) => (
									<MenuItem key={f.factorID} value={String(f.factorID)}>
										{f.name} ({f.factorValue} kg CO₂e{f.unit ? `/${f.unit}` : ""})
									</MenuItem>
								))}
							</Select>
						</FormControl>
					)}
					{needsQuantity ? (
						<Box sx={{ display: "flex", gap: 2 }}>
							<TextField
								size="small"
								label="Quantity"
								type="number"
								value={form.quantity}
								onChange={(e) => setForm({ ...form, quantity: e.target.value })}
								sx={{ flex: 1 }}
							/>
							<TextField
								size="small"
								label="Unit (kg, t, kWh…)"
								value={form.unit}
								onChange={(e) => setForm({ ...form, unit: e.target.value })}
								sx={{ flex: 1 }}
							/>
						</Box>
					) : (
						<TextField
							size="small"
							label="Spend (AUD)"
							type="number"
							value={form.spendAUD}
							onChange={(e) => setForm({ ...form, spendAUD: e.target.value })}
						/>
					)}
					{!form.customFactorID && (
						<TextField
							size="small"
							label={form.method === "spend" ? "Intensity (kg CO₂e per AUD)" : "Factor (kg CO₂e per unit)"}
							type="number"
							value={form.factorValue}
							onChange={(e) => setForm({ ...form, factorValue: e.target.value })}
							helperText={form.method === "average" ? "From a published dataset (NGA, EPD, ecoinvent…) — note the source below." : undefined}
						/>
					)}
					<TextField
						size="small"
						label="Factor source (optional)"
						value={form.factorSource}
						onChange={(e) => setForm({ ...form, factorSource: e.target.value })}
					/>
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setDialogOpen(false)}>Cancel</Button>
					<Button variant="contained" onClick={save} disabled={saving}>
						{saving ? "Saving…" : "Save entry"}
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={csvOpen} onClose={() => setCsvOpen(false)} fullWidth maxWidth="md">
				<DialogTitle>Import AP spend CSV ({csvRows.length - 1} rows)</DialogTitle>
				<DialogContent dividers>
					<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
						Map your columns. Rows import as spend-method entries; a
						category or factor column overrides the defaults below.
						The mapping is remembered for next time.
					</Typography>
					<Box sx={{ display: "grid", gap: 2, gridTemplateColumns: "1fr 1fr" }}>
						{MAPPING_FIELDS.map((f) => (
							<FormControl key={f.key} size="small">
								<InputLabel id={`map-${f.key}`}>
									{f.label}{f.required ? " *" : ""}
								</InputLabel>
								<Select
									labelId={`map-${f.key}`}
									label={`${f.label}${f.required ? " *" : ""}`}
									value={csvMapping[f.key] ?? ""}
									onChange={(e) =>
										setCsvMapping({
											...csvMapping,
											[f.key]: e.target.value === "" ? "" : Number(e.target.value),
										})
									}
								>
									<MenuItem value="">— not in file —</MenuItem>
									{csvRows[0]?.map((h, i) => (
										<MenuItem key={i} value={i}>{h || `Column ${i + 1}`}</MenuItem>
									))}
								</Select>
							</FormControl>
						))}
						<FormControl size="small">
							<InputLabel id="csv-cat">Default category</InputLabel>
							<Select labelId="csv-cat" label="Default category" value={csvCategory} onChange={(e) => setCsvCategory(Number(e.target.value))}>
								{GHG_CATEGORIES.map((c) => (
									<MenuItem key={c.id} value={c.id}>{c.id}. {c.name}</MenuItem>
								))}
							</Select>
						</FormControl>
						<TextField
							size="small"
							label="Default intensity (kg CO₂e per AUD)"
							type="number"
							value={csvFactor}
							onChange={(e) => setCsvFactor(e.target.value)}
						/>
					</Box>
					{csvRows.length > 1 && (
						<Box sx={{ mt: 2, overflowX: "auto" }}>
							<Typography variant="caption" color="text.secondary">Preview (first 5 rows)</Typography>
							<Table size="small">
								<TableHead>
									<TableRow>
										{csvRows[0].map((h, i) => (
											<TableCell key={i}>{h}</TableCell>
										))}
									</TableRow>
								</TableHead>
								<TableBody>
									{csvRows.slice(1, 6).map((r, i) => (
										<TableRow key={i}>
											{r.map((c, j) => (
												<TableCell key={j}>{c}</TableCell>
											))}
										</TableRow>
									))}
								</TableBody>
							</Table>
						</Box>
					)}
				</DialogContent>
				<DialogActions>
					<Button onClick={() => setCsvOpen(false)}>Cancel</Button>
					<Button
						variant="contained"
						onClick={runImport}
						disabled={
							importing ||
							csvMapping.description === undefined ||
							csvMapping.description === "" ||
							csvMapping.spendAUD === undefined ||
							csvMapping.spendAUD === ""
						}
					>
						{importing ? "Importing…" : "Import"}
					</Button>
				</DialogActions>
			</Dialog>
		</Container>
	);
};

export default Scope3Entries;
